import { TemplateResult, html, nothing, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { BlocksElement } from '../BlocksElement';
import { SlotController } from '../../controllers/slot-controller';
import type { StatusIndicatorType } from './status-indicator.model';
import styles from './status-indicator.styles';

/**
 * @csspart base - The component's base wrapper.
 */
export class BlocksStatusIndicator extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * The status indicator text
   */
  @property({ type: String, reflect: true })
  text: string | undefined;

  /**
   * Variant
   * <cat-text-passage size="sm">
   * <p>By default the status indicator is blue. (Neutral variant)</p>
   * <ul>
   * <li> **active** makes the status indicator green</li>
   * <li> **warning** makes the status indicator orange</li>
   * <li> **error** makes the status indicator red</li>
   * <li> **in-determined** makes the status indicator gray</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String, reflect: true })
  variant: 'active' | 'warning' | 'error' | 'in-determined' | undefined;

  /**
   * Size Variant
   * <ul>
   * <li> **default** - the standard size for the Status Indicator</li>
   * <li> **sm** - small size Status Indicator</li>
   * </ul>
   */
  @property({ type: String })
  size: 'sm' | undefined;

  /**
   * Disabled attribute
   */
  @property({ type: Boolean })
  disabled: boolean | undefined;

  /**
   * Type variant
   * <cat-text-passage size="sm">
   * <p>By default renders the text inside a pill shape with a background color.</p>
   * <ul>
   * <li> **bare** renders the text with a dot indicator and without a background color</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  type: StatusIndicatorType;

  protected slots = new SlotController(this, 'before');

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-status-indicator', {
      'cat-c-status-indicator--active': this.variant === 'active',
      'cat-c-status-indicator--warning': this.variant === 'warning',
      'cat-c-status-indicator--error': this.variant === 'error',
      'cat-c-status-indicator--indetermined': this.variant === 'in-determined',
      'cat-c-status-indicator--sm': this.size === 'sm',
      'cat-c-status-indicator--bare': this.type === 'bare',
      'cat-c-status-indicator--disabled': this.disabled === true,
      'cat-c-status-indicator--has-before-slot': this.slots.hasSlotted('before') && this.type === 'bare'
    });

    return html`
      <div part="base" class="${componentClassNames}">
        <div part="icon" class="cat-c-status-indicator__icon">
          <slot part="part-before" name="before"></slot>
        </div>
        ${this.text ? html` <span part="text">${this.text}</span> ` : nothing}
      </div>
    `;
  }
}

if (customElements.get('cat-status-indicator') === undefined) {
  customElements.define('cat-status-indicator', BlocksStatusIndicator);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-status-indicator': BlocksStatusIndicator;
  }
}
